// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useCallback, useMemo, useContext } from 'react';

import type { Project } from '@evolvemep/api-ts';
import { Card, Flex, Stack, Text, Title, LoadingOverlay } from '@mantine/core';

import type { GlobalNavigationItemLabel } from 'app/Layout/NavBar/globalNavigationMenu';
import { ProjectSelect } from 'components/Mantine/GlobalFilters/ProjectSelect';
import { useUpdateGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import type { PageTitle } from 'components/Mantine/Navigation/types';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';

type ProjectRequiredType = {
  selectedProject: Project;
  setSelectedProject: (project: Project | null) => void;
};

const ProjectRequiredContext = React.createContext<ProjectRequiredType | undefined>(undefined);

/**
 * Used when a Project *must* be selected
 * in order to render the child page
 */
export const ProjectRequired = ({
  title,
  creatable,
  loading,
  children,
}: {
  title: PageTitle | GlobalNavigationItemLabel;
  creatable?: boolean;
  loading?: boolean;
  children: ReactNode | ((project: Project) => ReactNode);
}) => {
  const {
    globalFilters: { projects },
    updateGlobalFilters,
  } = useUpdateGlobalFilters();
  const selectedProject = useMemo(() => (hasLengthExactly(projects, 1) ? projects[0] : null), [projects]);

  const onProjectSelected = useCallback(
    (project: Project | null) => updateGlobalFilters({ projects: project ? [project] : [] }),
    [updateGlobalFilters],
  );

  const value = useMemo(
    () => ({ selectedProject, setSelectedProject: onProjectSelected }),
    [onProjectSelected, selectedProject],
  );

  if (isNil(value.selectedProject)) {
    return (
      <Flex flex={1} align="center" justify="center">
        <Card shadow="md" withBorder>
          <Stack mb="sm">
            <Title order={4} fw={500} mt="xs" c="gray.7">
              Select a project to continue
            </Title>
            <Text>
              <b>{title}</b> requires you to first select a project.
            </Text>
            <ProjectSelect onChange={onProjectSelected} value={selectedProject} creatable={creatable} />
          </Stack>
          <LoadingOverlay visible={loading} />
        </Card>
      </Flex>
    );
  }

  return (
    <ProjectRequiredContext value={value as ProjectRequiredType}>
      {typeof children === 'function' ? children(value.selectedProject) : children}
    </ProjectRequiredContext>
  );
};

export const useProjectRequired = () => useGeneralContext(ProjectRequiredContext, 'ProjectRequired');
export const useProjectOptional = () => useContext(ProjectRequiredContext);
